import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  QrCode,
  Search,
  ShoppingCart,
  DollarSign,
  Wifi,
  WifiOff,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  UserCheck,
  RotateCw,
  ArrowLeft,
  Trash2,
  User,
  LogOut
} from 'lucide-react';
import { acreditacionApi } from '../services/acreditacionApi';
import { offlineStorageService } from '../services/offlineStorageService';
import { authService } from '../services/authService';
import {
  AcreditacionResult,
  NominacionPuertaRequest,
  AsistenciaOfflineItem
} from '../types/acreditacion.types';
import { OperadorSesion } from '../types/auth.types';
import { audioFeedbackService } from '../services/audioFeedbackService';
import { LoginOperadorView } from '../components/acreditacion/LoginOperadorView';
import { QrScannerModal } from '../components/acreditacion/QrScannerModal';
import { NominacionPuertaModal } from '../components/acreditacion/NominacionPuertaModal';
import { VentaPuertaForm } from '../components/acreditacion/VentaPuertaForm';
import { ArqueoCajaPanel } from '../components/acreditacion/ArqueoCajaPanel';

export const AcreditacionView: React.FC = () => {
  const [operador, setOperador] = useState<OperadorSesion | null>(() => authService.getOperador());
  const [activeTab, setActiveTab] = useState<'ESCANER' | 'BUSQUEDA' | 'VENTA' | 'ARQUEO' | 'OFFLINE'>('ESCANER');
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [scannerOpen, setScannerOpen] = useState<boolean>(false);
  const [nominarModalOpen, setNominarModalOpen] = useState<boolean>(false);
  const [pendingQrToNominate, setPendingQrToNominate] = useState<string>('');

  // Estados de resultado
  const [lastResult, setLastResult] = useState<AcreditacionResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Búsqueda Manual
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<AcreditacionResult[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);

  // Cola Offline
  const [offlineQueue, setOfflineQueue] = useState<AsistenciaOfflineItem[]>([]);
  const [syncingOffline, setSyncingOffline] = useState<boolean>(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cargar cola offline
    setOfflineQueue(offlineStorageService.obtenerColaOffline());

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Si no está autenticado, renderizar pantalla de Login de Operador
  if (!operador) {
    return <LoginOperadorView onLoginSuccess={(op) => setOperador(op)} />;
  }

  const tienePermisoVenta = authService.hasPermission('Caja.Web.Congreso.Ventas.Puerta') || authService.hasPermission('Caja.Web.Congreso.Vender');
  const tipoEntidad = operador.tipoEntidad ?? authService.getTipoEntidad();
  const ambitoHabilitado = tipoEntidad === 1 || tipoEntidad === 2 || tipoEntidad === 3;
  const puedeOperarCaja = tienePermisoVenta && ambitoHabilitado;

  const tabsDisponibles = [
    { id: 'ESCANER', label: 'Escanear QR', icon: QrCode },
    { id: 'BUSQUEDA', label: 'Búsqueda Manual', icon: Search },
    ...(puedeOperarCaja
      ? [
          { id: 'VENTA', label: 'Venta en Puerta', icon: ShoppingCart },
          { id: 'ARQUEO', label: 'Arqueo de Caja', icon: DollarSign }
        ]
      : []),
    { id: 'OFFLINE', label: `Cola Offline (${offlineQueue.length})`, icon: RotateCw }
  ];

  const emitirFeedbackSonoro = (res: AcreditacionResult) => {
    if (res.estadoCodigo === 1) audioFeedbackService.playSound('success');
    else if (res.estadoCodigo === 2) audioFeedbackService.playSound('warning');
    else if (res.estadoCodigo === 4) audioFeedbackService.playSound('nominate');
    else audioFeedbackService.playSound('error');
  };

  const procesarCodigoQr = async (codigoQr: string) => {
    if (!codigoQr) return;

    if (!isOnline) {
      // Modo Offline Activo: Guardar en cola local
      const item = offlineStorageService.guardarLecturaOffline(codigoQr, operador?.usuario || 'OperadorLocal');
      setOfflineQueue(offlineStorageService.obtenerColaOffline());

      const offlineRes: AcreditacionResult = {
        estadoCodigo: 1,
        mensaje: `Lectura almacenada en modo offline local (${item.codigoQr}). Se sincronizará al restablecer la conexión.`,
        nombrePortador: 'Guardado Local Offline',
        tipoEntrada: 'Offline Queue'
      };
      setLastResult(offlineRes);
      emitirFeedbackSonoro(offlineRes);
      return;
    }

    try {
      setLoading(true);
      const res = await acreditacionApi.acreditarQr(codigoQr);
      setLastResult(res);
      emitirFeedbackSonoro(res);

      if (res.estadoCodigo === 4) {
        // Requiere nominación
        setPendingQrToNominate(codigoQr);
        setNominarModalOpen(true);
      }
    } catch (err: any) {
      const errRes: AcreditacionResult = {
        estadoCodigo: 3,
        mensaje: err?.response?.data?.mensaje || err?.message || 'Error de conexión al servidor de acreditación.'
      };
      setLastResult(errRes);
      emitirFeedbackSonoro(errRes);
    } finally {
      setLoading(false);
    }
  };

  const handleNominacionSubmit = async (req: NominacionPuertaRequest) => {
    try {
      setLoading(true);
      const res = await acreditacionApi.nominarPuerta(req);
      setLastResult(res);
      emitirFeedbackSonoro(res);
      setNominarModalOpen(false);
    } catch (err: any) {
      const errRes: AcreditacionResult = {
        estadoCodigo: 3,
        mensaje: err?.response?.data?.mensaje || err?.message || 'Error nominando entrada en puerta.'
      };
      setLastResult(errRes);
      emitirFeedbackSonoro(errRes);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarManual = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    try {
      setSearchLoading(true);
      const data = await acreditacionApi.buscarManual(1, searchTerm.trim());
      setSearchResults(data);
    } catch (err) {
      console.error('Error buscando manual:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSincronizarOffline = async () => {
    const queue = offlineStorageService.obtenerColaOffline();
    if (queue.length === 0) return;

    try {
      setSyncingOffline(true);
      const res = await acreditacionApi.sincronizarLoteOffline(queue);
      offlineStorageService.limpiarColaOffline();
      setOfflineQueue([]);
      alert(`Sincronización exitosa: ${res.sincronizadas} acreditaciones registradas en el servidor.`);
    } catch (err) {
      console.error('Error sincronizando offline:', err);
      alert('Error sincronizando lote offline. Verifique la conexión con el servidor.');
    } finally {
      setSyncingOffline(false);
    }
  };

  const handleCerrarSesion = () => {
    authService.logout();
    setOperador(null);
  };

  return (
    <div className="min-h-screen bg-brand-dark text-slate-100 flex flex-col font-sans">
      {/* Barra Superior Operativa */}
      <header className="glass-nav py-4 px-4 sm:px-8 border-b border-brand-cyan/20 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="p-2 rounded-xl bg-brand-surface hover:bg-brand-navy text-slate-300 hover:text-white transition-colors"
            title="Volver al Micrositio"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>

          <div>
            <h1 className="font-heading font-extrabold text-lg text-white flex items-center gap-2">
              <span>Portal de Acreditación & Puerta</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand-cyan/20 text-brand-sky border border-brand-cyan/30">
                PWA Operador
              </span>
            </h1>
            <p className="text-xs text-slate-400">Congreso Inmobiliario 2026 • Sede Paseo Aldrey</p>
          </div>
        </div>

        {/* Info de Operador, Indicador de Conexión y Logout */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Badge de Operador Logueado */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-surface/80 border border-brand-cyan/30 text-xs text-slate-200">
            <User className="w-3.5 h-3.5 text-brand-cyan" />
            <span className="font-semibold">{operador.nombreCompleto || operador.usuario}</span>
          </div>

          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${
            isOnline
              ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-400'
              : 'bg-amber-950/60 border-amber-500/40 text-amber-400'
          }`}>
            {isOnline ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            <span>{isOnline ? 'Online' : 'Offline'}</span>
          </div>

          {offlineQueue.length > 0 && (
            <button
              onClick={() => setActiveTab('OFFLINE')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-cyan/20 border border-brand-cyan/40 text-brand-sky text-xs font-bold animate-pulse"
            >
              <RotateCw className="w-3.5 h-3.5" />
              <span>{offlineQueue.length} pendientes</span>
            </button>
          )}

          <button
            onClick={handleCerrarSesion}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-950/50 hover:bg-red-900 border border-red-500/40 text-red-300 hover:text-white text-xs font-semibold transition-colors"
            title="Cerrar Sesión"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Salir</span>
          </button>
        </div>
      </header>

      {/* Tabs de Navegación Operativa Superior (Desktop / Tablet Apaisada) */}
      <div className="hidden sm:block bg-brand-surface/40 border-b border-brand-cyan/15 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto flex overflow-x-auto gap-2 py-2">
          {tabsDisponibles.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-heading font-bold text-xs uppercase tracking-wider whitespace-nowrap transition-all ${
                  active
                    ? 'bg-brand-cyan text-brand-dark shadow-glow-cyan'
                    : 'text-slate-400 hover:text-white hover:bg-brand-surface'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenido Principal según Pestaña Activa con padding inferior para Bottom Dock móvil */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-8 pb-28 sm:pb-8">
        {/* Pestaña 1: Escáner QR */}
        {activeTab === 'ESCANER' && (
          <div className="max-w-xl mx-auto space-y-6 text-center">
            <div className="card-blueprint rounded-3xl p-8 shadow-2xl flex flex-col items-center">
              <button
                onClick={() => setScannerOpen(true)}
                className="w-32 h-32 rounded-3xl bg-brand-surface hover:bg-brand-navy border-2 border-brand-cyan/50 hover:border-brand-cyan flex flex-col items-center justify-center text-brand-cyan hover:text-white transition-all shadow-glow-cyan hover:scale-105 mb-6 group cursor-pointer"
              >
                <QrCode className="w-14 h-14 group-hover:scale-110 transition-transform" />
                <span className="text-[10px] uppercase font-bold tracking-wider mt-2">Abrir Cámara</span>
              </button>

              <h2 className="text-2xl font-extrabold text-white mb-2">
                Escanear Acreditación
              </h2>
              <p className="text-xs text-slate-300 mb-6">
                Presioná el botón para abrir el visor o ingresá el código alfanumérico.
              </p>

              {/* Formulario rápido en pantalla */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const input = (e.currentTarget.elements.namedItem('qrInput') as HTMLInputElement);
                  if (input.value) {
                    procesarCodigoQr(input.value);
                    input.value = '';
                  }
                }}
                className="w-full flex gap-2"
              >
                <input
                  name="qrInput"
                  type="text"
                  placeholder="Escanear o tipear código..."
                  className="flex-1 px-4 py-3 rounded-xl bg-brand-dark border border-brand-cyan/40 text-white font-mono text-sm focus:outline-none focus:border-brand-cyan uppercase"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 rounded-xl bg-brand-cyan hover:bg-brand-sky text-brand-dark font-heading font-extrabold text-xs uppercase transition-all shadow-glow-cyan"
                >
                  {loading ? 'Validando...' : 'Verificar'}
                </button>
              </form>
            </div>

            {/* Resultado Visual de la Acreditación */}
            {lastResult && (
              <div className={`card-blueprint rounded-3xl p-6 text-left border-2 shadow-2xl transition-all ${
                lastResult.estadoCodigo === 1
                  ? 'border-emerald-500 bg-emerald-950/30'
                  : lastResult.estadoCodigo === 2
                  ? 'border-amber-500 bg-amber-950/30'
                  : lastResult.estadoCodigo === 4
                  ? 'border-purple-500 bg-purple-950/30'
                  : 'border-red-500 bg-red-950/30'
              }`}>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-2xl bg-brand-dark/80 flex-shrink-0">
                    {lastResult.estadoCodigo === 1 && <CheckCircle2 className="w-8 h-8 text-emerald-400" />}
                    {lastResult.estadoCodigo === 2 && <AlertTriangle className="w-8 h-8 text-amber-400" />}
                    {lastResult.estadoCodigo === 4 && <UserCheck className="w-8 h-8 text-purple-400" />}
                    {lastResult.estadoCodigo === 3 && <XCircle className="w-8 h-8 text-red-400" />}
                  </div>

                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        {lastResult.tipoEntrada || 'Entrada'}
                      </span>
                      {lastResult.colegioNombre && (
                        <span className="text-xs px-2 py-0.5 rounded bg-brand-dark font-mono text-brand-sky">
                          {lastResult.colegioNombre}
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl font-extrabold text-white">
                      {lastResult.nombrePortador || 'Acceso Registrado'}
                    </h3>

                    {lastResult.dniPortador && (
                      <p className="text-xs text-slate-300 font-mono">DNI: {lastResult.dniPortador}</p>
                    )}

                    <p className="text-xs font-medium text-slate-200 pt-2 leading-relaxed">
                      {lastResult.mensaje}
                    </p>

                    {lastResult.usuarioAcreditador && (
                      <div className="pt-2 text-[11px] text-slate-400">
                        Acreditado por: <span className="text-brand-sky font-semibold">{lastResult.usuarioAcreditador}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pestaña 2: Búsqueda Manual */}
        {activeTab === 'BUSQUEDA' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="card-blueprint rounded-3xl p-6 sm:p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-2">Búsqueda Manual de Asistentes</h3>
              <p className="text-xs text-slate-400 mb-6">
                Búsqueda dual: Localizá entradas ingresando el DNI del afiliado adquirente o del portador asignado, matrícula o n° de entrada.
              </p>

              <form onSubmit={handleBuscarManual} className="flex gap-3 mb-6">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="DNI de Afiliado o Portador / Matrícula / N° Entrada..."
                  className="flex-1 px-4 py-3 rounded-xl bg-brand-dark border border-brand-cyan/40 text-white text-sm focus:outline-none focus:border-brand-cyan"
                />
                <button
                  type="submit"
                  disabled={searchLoading}
                  className="px-6 py-3 rounded-xl bg-brand-cyan hover:bg-brand-sky text-brand-dark font-heading font-extrabold text-xs uppercase transition-all shadow-glow-cyan flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  <span>{searchLoading ? 'Buscando...' : 'Buscar'}</span>
                </button>
              </form>

              {/* Resultados */}
              {searchResults.length > 0 ? (
                <div className="divide-y divide-slate-800 rounded-2xl border border-brand-cyan/20 overflow-hidden bg-brand-dark/60">
                  {searchResults.map((item, idx) => (
                    <div key={idx} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="font-bold text-white text-base">{item.nombrePortador}</div>
                        <div className="text-xs text-slate-400 flex items-center gap-3 mt-1">
                          <span>DNI: {item.dniPortador}</span>
                          <span>•</span>
                          <span>Colegio: {item.colegioNombre}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-3 py-1 rounded-full font-bold uppercase ${
                          item.estadoCodigo === 2
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                            : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                        }`}>
                          {item.estadoCodigo === 2 ? 'Acreditado' : 'Disponible'}
                        </span>
                        {item.estadoCodigo !== 2 && item.codigoQr && (
                          <button
                            onClick={() => {
                              procesarCodigoQr(item.codigoQr!);
                              setActiveTab('ESCANER');
                            }}
                            className="px-3 py-1.5 rounded-xl bg-brand-cyan hover:bg-brand-sky text-brand-dark font-heading font-extrabold text-xs uppercase flex items-center gap-1.5 transition-all shadow-glow-cyan"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>Acreditar</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchTerm && !searchLoading ? (
                <div className="text-center py-8 text-xs text-slate-400">
                  No se encontraron entradas para el término buscado.
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Pestaña 3: Venta en Puerta */}
        {activeTab === 'VENTA' && (
          <VentaPuertaForm
            idCongreso={1}
            onVentaCompletada={(res) => {
              setLastResult(res);
              emitirFeedbackSonoro(res);
              setActiveTab('ESCANER');
            }}
          />
        )}

        {/* Pestaña 4: Arqueo de Caja */}
        {activeTab === 'ARQUEO' && (
          <ArqueoCajaPanel />
        )}

        {/* Pestaña 5: Cola Offline */}
        {activeTab === 'OFFLINE' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="card-blueprint rounded-3xl p-6 sm:p-8 shadow-2xl">
              <div className="flex items-center justify-between pb-4 border-b border-slate-700/50 mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">Cola de Acreditaciones Offline</h3>
                  <p className="text-xs text-slate-400">
                    Lecturas realizadas sin conexión a internet pendientes de sincronizar con el servidor.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      offlineStorageService.limpiarColaOffline();
                      setOfflineQueue([]);
                    }}
                    className="p-2.5 rounded-xl bg-brand-surface hover:bg-red-950 text-slate-400 hover:text-red-400 border border-slate-700 transition-colors"
                    title="Vaciar cola"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <button
                    onClick={handleSincronizarOffline}
                    disabled={syncingOffline || offlineQueue.length === 0 || !isOnline}
                    className="px-4 py-2.5 rounded-xl bg-brand-cyan hover:bg-brand-sky text-brand-dark font-heading font-extrabold text-xs uppercase flex items-center gap-2 transition-all shadow-glow-cyan disabled:opacity-50"
                  >
                    <RotateCw className={`w-4 h-4 ${syncingOffline ? 'animate-spin' : ''}`} />
                    <span>{syncingOffline ? 'Sincronizando...' : 'Sincronizar Lote'}</span>
                  </button>
                </div>
              </div>

              {offlineQueue.length > 0 ? (
                <div className="divide-y divide-slate-800 rounded-2xl border border-brand-cyan/20 overflow-hidden bg-brand-dark/60">
                  {offlineQueue.map((item) => (
                    <div key={item.id} className="p-4 flex items-center justify-between text-xs">
                      <div>
                        <div className="font-mono font-bold text-brand-sky text-sm">{item.codigoQr}</div>
                        <div className="text-slate-400 mt-0.5">
                          Leído: {new Date(item.fechaLectura).toLocaleTimeString('es-AR')} • Operador: {item.usuarioOperador}
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-bold uppercase">
                        Pendiente
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400 text-xs">
                  No hay lecturas pendientes en la cola offline.
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Modales Operativos */}
      <QrScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={(code) => procesarCodigoQr(code)}
      />

      <NominacionPuertaModal
        isOpen={nominarModalOpen}
        codigoQr={pendingQrToNominate}
        onClose={() => setNominarModalOpen(false)}
        onSubmit={handleNominacionSubmit}
      />

      {/* Barra de Navegación Inferior Fija (Mobile Bottom Dock - Zona del Pulgar) */}
      <nav
        aria-label="Navegación Móvil Operador"
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-brand-navy/95 border-t border-brand-cyan/20 backdrop-blur-xl px-2 py-1 shadow-2xl flex items-center justify-around"
      >
        {tabsDisponibles.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={`mobile-${tab.id}`}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 py-2 px-1 flex flex-col items-center justify-center gap-1 min-h-[48px] touch-manipulation transition-all ${
                active
                  ? 'text-brand-cyan font-bold scale-105'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <div className="relative">
                <Icon className="w-5 h-5" />
                {tab.id === 'OFFLINE' && offlineQueue.length > 0 && (
                  <span className="absolute -top-1.5 -right-2 w-4 h-4 rounded-full bg-amber-500 text-brand-dark text-[9px] font-black flex items-center justify-center">
                    {offlineQueue.length}
                  </span>
                )}
              </div>
              <span className="text-[10px] uppercase tracking-tighter truncate max-w-[64px]">
                {tab.id === 'ESCANER'
                  ? 'Escáner'
                  : tab.id === 'BUSQUEDA'
                  ? 'Buscar'
                  : tab.id === 'VENTA'
                  ? 'Venta'
                  : tab.id === 'ARQUEO'
                  ? 'Arqueo'
                  : 'Offline'}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};
